import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { parse, isValid } from "date-fns"
import { DATE_FORMAT, FIELD_ANCHOR_ID_PREFIX, normalizeCultureCode } from "@/components/ats-fbo/constants"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { isStatusBlocked as isStatusBlockedDT } from "@/components/ats-fbo/decision-tables"
import type {
  Placement,
  PlacementFormState,
  PlacementFormErrors,
  ProfileType,
  Lob,
  WorkerProfile,
  TemplateV2,
} from "@/components/ats-fbo/types"
import type { CurrencyItem } from "@/components/ats-fbo/fields/numeric-field"
import type { SearchableSelectOption } from "@/components/ats-fbo/fields/searchable-select"
import type { ContactStatus } from "@/components/ats-fbo/types"
import { useAuth } from "@/contexts/auth-context"
import { useOrganizationSearch, useWorkerOrgSearch } from "@/hooks/use-organization-search"
import { useWorkerProfiles, useWorkerProfileLookup } from "@/hooks/use-worker-profiles"
import { useWorkerLandscape } from "@/hooks/use-worker-landscape"
import { useTemplates } from "@/hooks/use-templates"
import type { ReverseMapperLookups } from "@/lib/placement-mappers"
import type { SubmitResult } from "@/api/onboarding-api.types"
import type { Result } from "@/types/result"
import {
  deriveInitialScenario,
  deriveScenarioOnContactSelect,
  deriveScenarioOnOrgChange,
  deriveAutoMatchByProfileType,
} from "@/lib/scenario-derivation"
import { CODE_VALUE_GROUPS } from "@/types/code-values"

// ---------------------------------------------------------------------------
// Types re-exported for consumers
// ---------------------------------------------------------------------------

/** One entry per distinct contact for the worker dropdown */
export interface ContactOption extends SearchableSelectOption {
  contactId: string
  profileTypeCodes: string[]
  email: string
  contactStatus: ContactStatus
}

export interface FormCorrection {
  description: string
  patch?: Partial<PlacementFormState>
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function computeCorrections(
  result: SubmitResult,
  form: PlacementFormState,
  getWorkerProfileById: (profileId: number) => Promise<{ Id: number; ContactId: number } | null>,
): Promise<FormCorrection[]> {
  const corrections: FormCorrection[] = []
  const outcome = result.outcome
  if (!outcome) return corrections

  const workerOrgStep = outcome.steps.find((s) => s.step === "WorkerOrg")
  const workerStep = outcome.steps.find((s) => s.step === "Worker")

  // WorkerOrg succeeded but form still says "new" — org was created with the correct role type so it
  // will appear in search results. Set the ID directly; useODataSearch auto-fetches when selectedId
  // is set and not yet in options, so the dropdown renders the label without manual intervention.
  if (
    form.workerOrgScenario?.kind === "new" &&
    workerOrgStep?.succeeded &&
    outcome.workerOrganizationId != null
  ) {
    const orgName = form.workerOrgScenario.organizationName
    corrections.push({
      description: `"${orgName}" was created. Clicking apply will select it as the worker organization.`,
      patch: {
        workerOrgScenario: { kind: "existing", organizationId: String(outcome.workerOrganizationId) },
      },
    })
  }

  // WorkerOrg conflicted — an org with this name already exists. Clear the selection and let
  // the user search by the name they entered. If it doesn't appear, there is a role-type mismatch.
  if (
    form.workerOrgScenario?.kind === "new" &&
    workerOrgStep &&
    !workerOrgStep.succeeded &&
    workerOrgStep.conflictingEntityId != null
  ) {
    const orgName = form.workerOrgScenario.organizationName
    corrections.push({
      description: `"${orgName}" already exists. Clicking apply will clear the worker organization field — search for it by name and select it before resubmitting.`,
      patch: { workerOrgScenario: null },
    })
  }

  // Worker succeeded but a later step failed — profile was created and outcome.workerProfileId is set.
  if (workerStep?.succeeded && outcome.workerProfileId != null) {
    if (form.workerScenario?.kind === "add-profile") {
      const profileId = String(outcome.workerProfileId)
      const { contactId } = form.workerScenario
      corrections.push({
        description: `A worker profile was created. Clicking apply will switch the worker field to use it.`,
        patch: {
          workerScenario: { kind: "existing-profile", profileId, contactId },
        },
      })
    } else if (form.workerScenario?.kind === "create-contact") {
      const profileId = outcome.workerProfileId
      const profile = await getWorkerProfileById(profileId)
      corrections.push({
        description: `A new worker contact and profile were created. Clicking apply will switch the worker field to use them.`,
        patch: profile != null
          ? { workerScenario: { kind: "existing-profile", profileId: String(profileId), contactId: String(profile.ContactId) } }
          : undefined,
      })
    }
  }

  // Worker conflicted — a profile already exists matching email + contactId + profileTypeCode + workerOrgId.
  if (
    !workerStep?.succeeded &&
    workerStep?.conflictingEntityId != null &&
    form.workerScenario?.kind === "add-profile"
  ) {
    const profileId = String(workerStep.conflictingEntityId)
    const { contactId } = form.workerScenario
    corrections.push({
      description: `A matching worker profile already exists. Clicking apply will switch the worker field to use it.`,
      patch: {
        workerScenario: { kind: "existing-profile", profileId, contactId },
      },
    })
  }

  return corrections
}

function buildInitialForm(placement: Placement, fallbackLanguage: string): PlacementFormState {
  // Preferred language preference order:
  //   1. The contact's existing preferredLanguage from the API (when known).
  //   2. The current internal user's locale (fallbackLanguage from useTranslation).
  // Both are normalized to a supported culture code so the dropdown always has a
  // matching option to render. Only sent to the backend when scenario === "create-contact".
  const initialPreferredCultureCode = normalizeCultureCode(
    placement.workerPreferredLanguage ?? fallbackLanguage,
  )
  return {
    internalCompanyId: placement.internalCompanyId,
    clientOrgId: placement.clientOrgId,
    workerEmail: placement.workerEmail,
    profileTypeCode: placement.profileTypeCode,
    workerScenario: null,
    workerOrgScenario: placement.workerOrgId
      ? { kind: "existing", organizationId: placement.workerOrgId }
      : null,
    workerPreferredCultureCode: initialPreferredCultureCode,
    startDate: placement.startDate,
    endDate: placement.endDate,
    billRate: placement.billRate != null ? String(placement.billRate) : "",
    payRate: placement.payRate != null ? String(placement.payRate) : "",
    billCurrencyCode: placement.billCurrencyCode,
    payCurrencyCode: placement.payCurrencyCode,
    lobCode: placement.lobCode,
    templateV2Id: placement.templateV2Id,
  }
}

function validate(
  form: PlacementFormState,
  isWorkerOrgRequired: boolean,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): PlacementFormErrors {
  const errors: PlacementFormErrors = {}
  if (!form.internalCompanyId) errors.internalCompanyId = t("errorInternalCompanyRequired")
  if (!form.clientOrgId) errors.clientOrgId = t("errorClientOrgRequired")
  if (!form.workerEmail) errors.workerEmail = t("errorEmailRequired")
  if (!form.profileTypeCode) errors.profileTypeCode = t("errorProfileTypeRequired")
  if (!form.workerScenario) errors.worker = t("errorWorkerRequired")
  if (isWorkerOrgRequired && !form.workerOrgScenario) {
    errors.workerOrgId = t("errorWorkerOrgRequired")
  }
  if (!form.lobCode) errors.lobCode = t("errorLobRequired")
  if (!form.startDate) errors.startDate = t("errorStartDateRequired")
  else if (!isValid(parse(form.startDate, DATE_FORMAT, new Date()))) errors.startDate = t("errorStartDateInvalid")
  if (!form.endDate) errors.endDate = t("errorEndDateRequired")
  else if (!isValid(parse(form.endDate, DATE_FORMAT, new Date()))) errors.endDate = t("errorEndDateInvalid")
  else if (form.startDate && isValid(parse(form.startDate, DATE_FORMAT, new Date())) && form.endDate < form.startDate)
    errors.endDate = t("errorEndBeforeStart")
  if (!form.billRate) errors.billRate = t("errorBillRateRequired")
  if (!form.payRate) errors.payRate = t("errorPayRateRequired")
  if (!form.billCurrencyCode) errors.billCurrencyCode = t("errorBillCurrencyRequired")
  if (!form.payCurrencyCode) errors.payCurrencyCode = t("errorPayCurrencyRequired")
  if (
    form.billRate &&
    form.payRate &&
    form.billCurrencyCode &&
    form.payCurrencyCode &&
    form.billCurrencyCode === form.payCurrencyCode &&
    parseFloat(form.payRate) > parseFloat(form.billRate)
  ) {
    errors.payRate = t("errorPayHigherThanBill")
  }
  return errors
}

const FIELD_LABEL_KEYS: Record<keyof PlacementFormErrors, TranslationKey> = {
  internalCompanyId: "labelInternalCompany",
  clientOrgId: "labelClientOrganization",
  workerEmail: "labelEmailAddress",
  profileTypeCode: "labelProfileType",
  worker: "labelWorker",
  workerOrgId: "labelWorkerOrganization",
  lobCode: "labelLineOfBusiness",
  startDate: "labelStartDate",
  endDate: "labelEndDate",
  billRate: "labelBillRate",
  payRate: "labelPayRate",
  billCurrencyCode: "labelBillCurrency",
  payCurrencyCode: "labelPayCurrency",
}

function normalizeCompareValue(value: string | null | undefined): string | null {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  return normalized.length > 0 ? normalized : null
}

/** Returns "YYYY-MM-DD" in the local timezone — used for placeholder org names. */
function formatLocalDate(date: Date): string {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Builds a placeholder Worker Organization name when the user opts into the
 * "I don't know organization name" mode. Format is `<workerName> - <YYYY-MM-DD>`,
 * falling back to the email when no display name is available. CS will rename
 * the org later once the real one is identified — the date suffix makes these
 * easy to find and disambiguate when bulk-fixing.
 */
export function buildPlaceholderWorkerOrgName(
  workerName: string | null | undefined,
  date: Date,
): string {
  const cleaned = workerName?.trim()
  const base = cleaned && cleaned.length > 0 ? cleaned : "Unknown worker"
  return `${base} - ${formatLocalDate(date)}`
}

// ---------------------------------------------------------------------------
// Hook input / output types
// ---------------------------------------------------------------------------

export interface UsePlacementFormProps {
  placement: Placement
  profileTypes: ProfileType[]
  lobs: Lob[]
  currencies: CurrencyItem[]
  fboAppUrl?: string
  onClose: () => void
  onSubmit: (form: PlacementFormState) => Promise<Result<SubmitResult>>
}

interface OrgSearchHook {
  options: SearchableSelectOption[]
  search: (term: string) => void
  loading: boolean
}

export interface UsePlacementFormReturn {
  form: PlacementFormState

  actions: {
    // Worker scenario
    selectContact: (contactId: string) => void
    createContact: () => void
    undoWorkerCreation: () => void
    // Worker org
    selectWorkerOrg: (orgId: string) => void
    /** Switch directly into existing-profile scenario for the given org's matching profile.
     * Used when the section's "Use existing profile" affordance fires after the user tried
     * to pick an org that's already linked to a same-type profile in this email's landscape. */
    useExistingProfileForOrg: (orgId: string) => void
    createWorkerOrg: (name: string) => void
    /** Switch the Worker Organization input into "search for an existing org" mode. Clears any previous org pick so the selector starts empty. */
    enterWorkerOrgSearchMode: () => void
    /** Switch the Worker Organization input into "I don't know organization name" mode. At submit time the backend gets a placeholder org named "<workerName> - YYYY-MM-DD". */
    enterWorkerOrgUnknownMode: () => void
    clearWorkerOrg: () => void
    unlockWorkerOrg: () => void
    /** Tells the form whether the email field has un-confirmed edits open. While true, `canSubmit` flips false and `disabledReason` explains the block. */
    setEmailEditing: (isEditing: boolean) => void
    // Simple field setters
    setWorkerEmail: (email: string) => void
    setInternalCompanyId: (id: string | null) => void
    setClientOrgId: (id: string | null) => void
    setProfileTypeCode: (code: string | null) => void
    setStartDate: (date: string) => void
    setEndDate: (date: string) => void
    setBillRate: (rate: string) => void
    setPayRate: (rate: string) => void
    setBillCurrencyCode: (code: string | null) => void
    setPayCurrencyCode: (code: string | null) => void
    setLobCode: (code: string | null) => void
    setTemplateV2Id: (id: string | null) => void
    setWorkerPreferredCultureCode: (code: string) => void
    // Submit lifecycle
    submit: () => Promise<void>
    applyCorrections: () => void
  }

  validation: {
    errors: PlacementFormErrors
    showError: (field: keyof PlacementFormErrors) => boolean
    canSubmit: boolean
    isBlocked: boolean
    /** True while the email field has un-confirmed edits open. UI uses this to keep Submit visibly disabled (not just no-op) so users see they need to confirm or cancel. */
    isEmailEditing: boolean
    showClientOrgBanner: boolean
    submitted: boolean
    disabledReason: string | null
    /** Ordered list of missing/invalid fields, with the user-facing label and the anchor id
     * for click-to-focus. Used by the footer to render a clickable list above the Proceed
     * button so the user can jump straight to the offending input. Empty when the form is
     * valid (or only blocked by status / email-editing reasons). */
    missingFields: Array<{ key: keyof PlacementFormErrors; label: string; anchorId: string }>
    hasMarginError: boolean
    payRateCurrencyWarning: string | undefined
  }

  submission: {
    isSubmitting: boolean
    result: SubmitResult | null
    corrections: FormCorrection[]
    succeeded: boolean
    isFormLocked: boolean
  }

  worker: {
    allProfiles: WorkerProfile[]
    contactOptions: ContactOption[]
    selectedContactId: string | null
    hasNameMismatch: boolean
    isStatusBlocked: boolean
    blockedProfileUrl: string | null
    loading: boolean
    showCreateContactAction: boolean
    /** True when the email's existing contacts already have a profile of the currently
     * selected profile type. The UI uses this to replace the "Add new worker contact" button
     * with a hard-block message — duplicate same-type profiles are almost never legitimate. */
    hasExistingProfileOfSelectedType: boolean
    profileTypeValue: string
    refreshKey: number
  }

  workerOrg: {
    isVisible: boolean
    isRequired: boolean
    isLocked: boolean
    /** True when the currently-selected worker profile already has an associated organization. UI uses this to disable "Create new" — that worker shouldn't be re-orged via creation. */
    selectedProfileHasOrg: boolean
    /** Which input mode the Worker Organization section is showing: "search" reveals the org selector; "unknown" shows the placeholder-creation banner. Defaults to "unknown" so users land on the safe-by-default "I don't know" mode. */
    inputMode: "search" | "unknown"
    /** True when the Search/I-don't-know radio toggle should be visible AND the placeholder
     * bypass at submit is honored. Tracks the same conditions as the validation/submit
     * guards in the hook: create-contact, or add-profile after manual unlock. */
    canChooseUnknownMode: boolean
    /** Preview of the placeholder org name that will be auto-created when in "unknown" mode, computed against today's date. Null when no worker name is resolvable yet. */
    placeholderName: string | null
    /** Org IDs already linked to a same-type profile in this email's worker landscape. The
     * UI shows these in search but intercepts the click — picking one would orphan a duplicate
     * Inc-Worker linkage, so the section warns and offers "Use existing profile" instead. */
    excludedOrgIds: Set<string>
    refreshKey: number
  }

  search: {
    internalCompany: OrgSearchHook
    clientOrg: OrgSearchHook
    workerOrg: OrgSearchHook
  }

  templates: {
    list: TemplateV2[]
    canSelect: boolean
    placeholder: string
  }
  creationItems: string[]

  diffs: {
    internalCompanyChanged: boolean
    clientOrgChanged: boolean
    profileTypeChanged: boolean
    workerChanged: boolean
    workerOrgChanged: boolean
    lobChanged: boolean
    emailChanged: boolean
    startDateChanged: boolean
    endDateChanged: boolean
    billRateChanged: boolean
    payRateChanged: boolean
    billCurrencyChanged: boolean
    payCurrencyChanged: boolean
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePlacementForm({
  placement,
  profileTypes,
  lobs,
  fboAppUrl,
  onClose,
  onSubmit,
}: UsePlacementFormProps): UsePlacementFormReturn {
  const { t, language } = useTranslation()
  const { token } = useAuth()
  const getToken = useCallback(() => Promise.resolve(token), [token])
  const getWorkerProfileById = useWorkerProfileLookup(getToken)

  const ats = placement.atsSource

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [form, setForm] = useState<PlacementFormState>(() => buildInitialForm(placement, language))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)
  const [pendingCorrections, setPendingCorrections] = useState<FormCorrection[]>([])
  const [workerProfilesRefreshKey, setWorkerProfilesRefreshKey] = useState(0)
  const [landscapeRefreshKey, setLandscapeRefreshKey] = useState(0)
  const [workerOrgRefreshKey, setWorkerOrgRefreshKey] = useState(0)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)
  const [isWorkerOrgManuallyUnlocked, setIsWorkerOrgManuallyUnlocked] = useState(false)
  // True while the email field has uncommitted edits open (the green-check / red-X state).
  // Submit is blocked in this state so users can't proceed with un-confirmed edits sitting
  // in the input — they must confirm or cancel first.
  const [isEmailEditing, setIsEmailEditing] = useState(false)
  // UI mode for the Worker Organization section. "search" is the default so users always start
  // by trying to find or create a real org. "I don't know" (placeholder) is opt-in and only
  // honored at submit time when the worker scenario is create-contact (brand-new worker). The
  // safety effect below forces "search" for existing-profile and add-profile regardless.
  const [workerOrgInputMode, setWorkerOrgInputMode] = useState<"search" | "unknown">("search")
  const [clientOrgAutoMatchPhase, setClientOrgAutoMatchPhase] = useState<"idle" | "searching" | "done">("idle")
  const skipOrgDeriveRef = useRef(false)

  // ---------------------------------------------------------------------------
  // Re-initialize when placement prop changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setForm(buildInitialForm(placement, language))
    setTouched({})
    setSubmitted(false)
    setIsWorkerOrgManuallyUnlocked(false)
    setIsEmailEditing(false)
    // Always start in search mode. Placeholder mode is opt-in and only valid for create-contact.
    setWorkerOrgInputMode("search")
    setClientOrgAutoMatchPhase("idle")
  }, [placement])

  // ---------------------------------------------------------------------------
  // External hooks
  // ---------------------------------------------------------------------------
  const internalCompanySearch = useOrganizationSearch("InternalRole", form.internalCompanyId, getToken)
  const clientOrgSearch = useOrganizationSearch("ClientRole", form.clientOrgId, getToken)
  const workerOrgSearchHook = useWorkerOrgSearch(
    form.profileTypeCode,
    form.workerOrgScenario?.kind === "existing" ? form.workerOrgScenario.organizationId : null,
    getToken,
  )

  const landscape = useWorkerLandscape(form.workerEmail, getToken, landscapeRefreshKey)

  const contactIds = useMemo(
    () => landscape.contactsForEmail.map((c) => c.contactId),
    [landscape.contactsForEmail]
  )
  const workerProfiles = useWorkerProfiles(contactIds, getToken, workerProfilesRefreshKey)
  const allProfiles = workerProfiles.allProfiles

  const { getFilteredTemplates } = useTemplates(getToken)

  // ---------------------------------------------------------------------------
  // Auto-match: try to pre-select client org from ATS name on initial load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (clientOrgAutoMatchPhase === "done") return

    const normalizedSourceClientOrgName = normalizeCompareValue(ats.clientOrgName)
    if (!normalizedSourceClientOrgName) {
      setClientOrgAutoMatchPhase("done")
      return
    }

    if (form.clientOrgId) {
      setClientOrgAutoMatchPhase("done")
      return
    }

    if (clientOrgAutoMatchPhase === "idle") {
      setClientOrgAutoMatchPhase("searching")
      clientOrgSearch.search(ats.clientOrgName.trim())
      return
    }

    if (clientOrgAutoMatchPhase === "searching" && !clientOrgSearch.loading) {
      const matches = clientOrgSearch.options.filter((option) => {
        const comparableName = option.canonicalName ?? option.name
        return normalizeCompareValue(comparableName) === normalizedSourceClientOrgName
      })

      if (matches.length === 1) {
        const [{ id }] = matches
        setForm((prev) => (prev.clientOrgId ? prev : { ...prev, clientOrgId: id }))
      }

      setClientOrgAutoMatchPhase("done")
    }
  }, [
    ats.clientOrgName,
    form.clientOrgId,
    clientOrgAutoMatchPhase,
    clientOrgSearch.loading,
    clientOrgSearch.options,
    clientOrgSearch.search,
  ])

  // ---------------------------------------------------------------------------
  // Profile type change: hard-reset worker + org so the user picks fresh
  // ---------------------------------------------------------------------------
  // The previous behavior tried to be clever — re-derive the scenario in place so the user
  // didn't have to re-pick a contact when switching profile types. That confused users:
  // they'd change profile type but the worker stayed selected (sometimes against a contact
  // that didn't match the new type), and the worker-org would silently clobber. We now wipe
  // both scenarios on every profile-type change. The on-load auto-match effect re-runs (it
  // gates on workerScenario === null) and re-picks a single matching profile if one exists.
  //
  // Declared BEFORE the auto-match effect so its setForm queues first: when scenarios are
  // already empty the noop guard short-circuits, leaving auto-match's update as the sole
  // queued change for this render. If we let auto-match queue first, this reset would
  // receive its result via `prev` and unconditionally overwrite the freshly-matched scenario.
  const prevProfileTypeRef = useRef(form.profileTypeCode)
  useEffect(() => {
    if (prevProfileTypeRef.current === form.profileTypeCode) return
    prevProfileTypeRef.current = form.profileTypeCode
    setIsWorkerOrgManuallyUnlocked(false)
    setWorkerOrgInputMode("search")
    // Only clobber when there's actually a stale pick to clear. When both scenarios are
    // already empty (e.g. right after an email change reset them), forcing a setForm here
    // would race the auto-match effect in the same render and overwrite the freshly-derived
    // scenario it queues — so skip it and let auto-match win.
    setForm((prev) => {
      const orgEmpty = prev.workerOrgScenario == null || prev.workerOrgScenario.kind === "none"
      if (prev.workerScenario == null && orgEmpty) return prev
      return { ...prev, workerScenario: null, workerOrgScenario: null }
    })
  }, [form.profileTypeCode])

  // ---------------------------------------------------------------------------
  // Auto-match: set initial scenario once profiles load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (form.workerScenario || workerProfiles.loading) return

    // Prefer the placement-driven match (workerId / workerContactId hint) but ONLY when the
    // user hasn't changed profile type or email yet. After a profile-type change the
    // placement's `workerId` still points at the original profile (wrong type); after an email
    // change the placement's `workerContactId` still points at the original email's contact —
    // either way the placement hint is stale and we should fall through to type-driven
    // auto-match against the now-loaded contacts for the current email.
    if (
      form.profileTypeCode === placement.profileTypeCode &&
      form.workerEmail === placement.workerEmail
    ) {
      const derived = deriveInitialScenario(placement, allProfiles)
      if (derived) {
        setForm((prev) => ({ ...prev, ...derived }))
        return
      }
    }

    // No (valid) placement hint: fall back to auto-matching the email's contacts by profile
    // type. Only fires when exactly one matching profile is found — multiple matches force
    // the user to pick explicitly. This is also what handles the post-profile-type-change
    // case: hook reset workerScenario to null, this effect picks up the matching profile
    // for the new type.
    if (form.profileTypeCode) {
      const autoMatched = deriveAutoMatchByProfileType(form.profileTypeCode, allProfiles)
      if (autoMatched) {
        setForm((prev) => ({ ...prev, ...autoMatched }))
      }
    }
  }, [placement, allProfiles, workerProfiles.loading, form.workerScenario, form.profileTypeCode, form.workerEmail])

  // ---------------------------------------------------------------------------
  // ESC key closes the panel
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  // ---------------------------------------------------------------------------
  // Org change: re-derive scenario after manual unlock
  // ---------------------------------------------------------------------------
  const effectiveWorkerOrgId = form.workerOrgScenario?.kind === "existing"
    ? form.workerOrgScenario.organizationId
    : null
  const prevWorkerOrgRef = useRef(effectiveWorkerOrgId)
  const selectedContactId = form.workerScenario && "contactId" in form.workerScenario
    ? form.workerScenario.contactId
    : null

  useEffect(() => {
    if (prevWorkerOrgRef.current !== effectiveWorkerOrgId) {
      prevWorkerOrgRef.current = effectiveWorkerOrgId

      // When corrections set both workerScenario and workerOrgScenario at once,
      // skip re-derivation — the correction patch already has the right scenario.
      if (skipOrgDeriveRef.current) {
        skipOrgDeriveRef.current = false
        return
      }

      // In "unknown" mode the user is intentionally NOT picking a real org. Snapping the
      // scenario back from whichever derivation matches the (now-empty) org would clobber the
      // existing-profile → add-profile transition done by enterWorkerOrgUnknownMode, leaving
      // the user looking at "I don't know" but with workerScenario silently restored to
      // existing-profile (where the placeholder swap is silently dropped at submit).
      if (workerOrgInputMode === "unknown") return

      if (!selectedContactId || !form.profileTypeCode) return
      if (form.workerOrgScenario?.kind === "new") return

      if (!effectiveWorkerOrgId) {
        const derived = deriveScenarioOnContactSelect(selectedContactId, form.profileTypeCode, allProfiles)
        setForm((prev) => ({ ...prev, ...derived }))
        return
      }

      const newScenario = deriveScenarioOnOrgChange(
        selectedContactId,
        effectiveWorkerOrgId,
        form.profileTypeCode,
        allProfiles,
      )
      setForm((prev) => ({ ...prev, workerScenario: newScenario }))
    }
  }, [effectiveWorkerOrgId, form.profileTypeCode, selectedContactId, allProfiles, workerOrgInputMode])

  // ---------------------------------------------------------------------------
  // Templates filtered by LOB + client org
  // ---------------------------------------------------------------------------
  const filteredTemplates = useMemo(() => {
    if (!form.lobCode || !form.clientOrgId) return []
    const lob = lobs.find((l) => l.code === form.lobCode)
    if (!lob) return []
    return getFilteredTemplates(
      form.clientOrgId,
      lob.id,
      form.lobCode,
      null,
    ).map((t) => ({ id: t.id, name: t.name, lobCode: form.lobCode!, clientOrgId: form.clientOrgId! }))
  }, [form.lobCode, form.clientOrgId, lobs, getFilteredTemplates])

  useEffect(() => {
    if (form.templateV2Id && !filteredTemplates.some((t) => t.id === form.templateV2Id)) {
      setForm((prev) => ({ ...prev, templateV2Id: null }))
    }
  }, [form.lobCode, form.clientOrgId, form.templateV2Id, filteredTemplates])

  // ---------------------------------------------------------------------------
  // Derived: worker org visibility / locking
  // ---------------------------------------------------------------------------
  const selectedProfileType = profileTypes.find((pt) => pt.code === form.profileTypeCode)
  const isWorkerOrgRequired = selectedProfileType?.orgRequired ?? false
  const isWorkerOrgVisible = isWorkerOrgRequired

  // Invariant: when org is not required (field is hidden), workerOrgScenario must
  // always be { kind: "none" }
  useEffect(() => {
    if (!isWorkerOrgRequired && form.workerOrgScenario === null) {
      setForm((prev) => ({ ...prev, workerOrgScenario: { kind: "none" } }))
    }
  }, [isWorkerOrgRequired, form.workerOrgScenario])

  const selectedWorkerScenario = form.workerScenario?.kind === "existing-profile"
    ? form.workerScenario
    : null
  const selectedWorker = selectedWorkerScenario
    ? allProfiles.find((p) => p.id === selectedWorkerScenario.profileId)
    : undefined

  const isWorkerOrgLocked =
    form.workerScenario?.kind === "existing-profile" &&
    !!selectedWorker?.workerOrgId &&
    !isWorkerOrgManuallyUnlocked

  // Force "search" when an existing-profile is selected (without manual unlock) — the field
  // is locked there anyway and mode is moot, but we keep state consistent. For add-profile
  // and create-contact the user is creating a fresh profile, so placeholder mode is honored
  // by the backend (the AddProfile/CreateContact branch creates the org and the profile from
  // the request) and we let the user choose freely. After a manual unlock on existing-profile
  // the user has explicitly opted into overriding the org; the unknown / new actions
  // transition the scenario to add-profile so any new org actually gets created.
  useEffect(() => {
    if (isWorkerOrgManuallyUnlocked) return
    const kind = form.workerScenario?.kind
    if (kind === "existing-profile" && workerOrgInputMode !== "search") {
      setWorkerOrgInputMode("search")
    }
  }, [form.workerScenario, workerOrgInputMode, isWorkerOrgManuallyUnlocked])

  // ---------------------------------------------------------------------------
  // Derived: contact options
  // ---------------------------------------------------------------------------
  const contactOptions = useMemo((): ContactOption[] => {
    const map = new Map<string, ContactOption>()
    for (const p of allProfiles) {
      const existing = map.get(p.contactId)
      if (existing) {
        if (!existing.profileTypeCodes.includes(p.profileTypeCode)) {
          existing.profileTypeCodes.push(p.profileTypeCode)
        }
      } else {
        map.set(p.contactId, {
          id: p.contactId,
          name: p.name,
          contactId: p.contactId,
          profileTypeCodes: [p.profileTypeCode],
          email: p.email,
          contactStatus: p.contactStatus,
        })
      }
    }
    return [...map.values()]
  }, [allProfiles])

  // ---------------------------------------------------------------------------
  // Derived: worker state
  // ---------------------------------------------------------------------------
  const selectedContactName = selectedContactId
    ? contactOptions.find((c) => c.id === selectedContactId)?.name
    : undefined

  /**
   * Display name used for placeholder Worker Organization creation
   * ("I don't know" mode). Resolution order:
   *   1. Existing profile name (when an existing profile is selected).
   *   2. Selected contact's display name (add-profile case).
   *   3. ATS-source worker name (create-contact case — that's what the form is showing).
   *   4. Worker email as a last-resort fallback.
   */
  const resolvedWorkerNameForPlaceholder = (() => {
    const scenario = form.workerScenario
    if (scenario?.kind === "existing-profile") {
      const profile = allProfiles.find((p) => p.id === scenario.profileId)
      if (profile?.name) return profile.name
    }
    if (selectedContactName) return selectedContactName
    if (ats.workerName) return ats.workerName
    if (form.workerEmail) return form.workerEmail
    return null
  })()

  const hasNameMismatch = !!(
    selectedContactName &&
    ats.workerName &&
    selectedContactName.trim().toLowerCase() !== ats.workerName.trim().toLowerCase()
  )

  const addProfileScenario =
    form.workerScenario?.kind === "add-profile" ? form.workerScenario : null

  const blockedProfile = addProfileScenario
    ? allProfiles.find(
        (p) =>
          p.contactId === addProfileScenario.contactId &&
          isStatusBlockedDT(undefined, p.profileStatus),
      ) ?? null
    : null

  const contactForBlocking = addProfileScenario
    ? contactOptions.find((c) => c.id === addProfileScenario.contactId)
    : undefined

  const isStatusBlocked = addProfileScenario
    ? isStatusBlockedDT(contactForBlocking?.contactStatus, blockedProfile?.profileStatus)
    : false

  const profileTypeValue =
    profileTypes.find((pt) => pt.code === form.profileTypeCode)?.value ?? "profile"

  // True when the email's existing contacts already include a profile of the selected type.
  // Used to STRENGTHEN the in-dropdown confirmation copy (so the user sees the conflict
  // explicitly), not to hard-block. There are legitimate reasons to create a new profile of
  // the same type — e.g. the user wants to associate a different organization with a new
  // profile rather than overriding the existing one.
  const hasExistingProfileOfSelectedType =
    !!form.profileTypeCode &&
    allProfiles.some((p) => p.profileTypeCode === form.profileTypeCode)

  // Org IDs already linked to a same-type profile in the email's worker landscape. The UI
  // doesn't HIDE these from search results (the user still sees them), but intercepts the
  // selection — picking such an org would link a new profile to an org that already has one
  // for this email, which is the exact "duplicate Inc Worker on the same org" foot-gun the
  // user reported. Keyed by orgId → matching profile so the section can offer a one-shot
  // "Use existing profile" switch into existing-profile scenario.
  const excludedOrgToProfile = useMemo((): Map<string, { profileId: string; contactId: string }> => {
    const kind = form.workerScenario?.kind
    if (kind !== "add-profile" && kind !== "create-contact") return new Map()
    if (!form.profileTypeCode) return new Map()
    const result = new Map<string, { profileId: string; contactId: string }>()
    for (const p of allProfiles) {
      if (p.profileTypeCode !== form.profileTypeCode) continue
      if (p.workerOrgId == null) continue
      // First occurrence wins; if multiple profiles point to the same org for the same type
      // (rare), the others can't be reached via this fallback either way.
      if (!result.has(p.workerOrgId)) {
        result.set(p.workerOrgId, { profileId: p.id, contactId: p.contactId })
      }
    }
    return result
  }, [allProfiles, form.profileTypeCode, form.workerScenario])

  // Stable Set of just the org IDs for the section's intercept check (Set lookups are O(1)
  // and the Set identity stays stable as long as the underlying map does).
  const excludedOrgIdSet = useMemo(
    () => new Set(excludedOrgToProfile.keys()),
    [excludedOrgToProfile],
  )

  const showCreateContactAction =
    !!form.profileTypeCode &&
    !landscape.loading &&
    !workerProfiles.loading &&
    form.workerScenario?.kind !== "create-contact"

  // ---------------------------------------------------------------------------
  // Derived: client org blocking
  // ---------------------------------------------------------------------------
  const isClientOrgMissing = !form.clientOrgId
  const hasNoMappedClient = !placement.clientOrgId && !!placement.atsSource.clientOrgName

  // ---------------------------------------------------------------------------
  // Derived: diffs
  // ---------------------------------------------------------------------------
  const resolveInternalCompanyName = (id: string | null) =>
    internalCompanySearch.options.find((c) => c.id === id)?.name ?? null
  const resolveClientOrgName = (id: string | null) => {
    const selected = clientOrgSearch.options.find((c) => c.id === id)
    return selected?.canonicalName ?? selected?.name ?? null
  }
  const resolveContactName = (cId: string | null) =>
    contactOptions.find((c) => c.id === cId)?.name ?? null
  const resolveWorkerOrgName = (id: string | null) => {
    const selected = workerOrgSearchHook.options.find((o) => o.id === id)
    return selected?.canonicalName ?? selected?.name ?? null
  }

  const clientOrgComparableName = resolveClientOrgName(form.clientOrgId)
  const hasResolvedClientOrg =
    !!form.clientOrgId &&
    normalizeCompareValue(clientOrgComparableName) != null

  const workerOrgComparableName =
    form.workerOrgScenario?.kind === "existing"
      ? resolveWorkerOrgName(form.workerOrgScenario.organizationId)
      : null
  const hasResolvedWorkerOrg =
    form.workerOrgScenario?.kind === "existing" &&
    normalizeCompareValue(workerOrgComparableName) != null

  const internalCompanyChanged =
    !!ats.internalCompanyName &&
    resolveInternalCompanyName(form.internalCompanyId) !== ats.internalCompanyName

  const clientOrgChanged =
    !!ats.clientOrgName &&
    hasResolvedClientOrg &&
    // An untouched pre-selection is not an override. Now that a mapped client arrives pre-selected, the
    // FBO name differing from the ATS name is the NORM for exactly those rows — it is the reason the id
    // mapping exists — so comparing names alone pilled a value the reviewer never touched. Every other
    // *Changed flag means "the reviewer overrode what the ATS said"; this keeps that meaning.
    // The name comparison stays: the auto-match fallback only fires on an exact match, so it reads as
    // unchanged either way.
    form.clientOrgId !== placement.clientOrgId &&
    normalizeCompareValue(clientOrgComparableName) !== normalizeCompareValue(ats.clientOrgName)

  const profileTypeChanged =
    !!ats.profileTypeCode &&
    form.profileTypeCode !== ats.profileTypeCode

  const workerChanged =
    !!ats.workerName &&
    selectedContactId != null &&
    resolveContactName(selectedContactId)?.trim().toLowerCase() !== ats.workerName.trim().toLowerCase()

  const workerOrgChanged =
    !!ats.workerOrgName &&
    hasResolvedWorkerOrg &&
    normalizeCompareValue(workerOrgComparableName) !== normalizeCompareValue(ats.workerOrgName)

  const startDateChanged = !!ats.startDate && form.startDate !== ats.startDate
  const endDateChanged = !!ats.endDate && form.endDate !== ats.endDate

  const billRateChanged =
    ats.billRate != null &&
    form.billRate !== "" &&
    parseFloat(form.billRate) !== ats.billRate

  const payRateChanged =
    ats.payRate != null &&
    form.payRate !== "" &&
    parseFloat(form.payRate) !== ats.payRate

  const billCurrencyChanged = !!ats.billCurrencyCode && form.billCurrencyCode !== ats.billCurrencyCode
  const payCurrencyChanged = !!ats.payCurrencyCode && form.payCurrencyCode !== ats.payCurrencyCode

  const lobChanged = !!ats.lobCode && form.lobCode !== ats.lobCode
  const emailChanged = !!ats.workerEmail && form.workerEmail !== ats.workerEmail

  // ---------------------------------------------------------------------------
  // Derived: creation items
  // ---------------------------------------------------------------------------
  const creationItems: string[] = []
  if (form.workerScenario?.kind === "create-contact") creationItems.push(t("creationItemWorkerContact"))
  if (form.workerScenario?.kind === "add-profile" || form.workerScenario?.kind === "create-contact") {
    creationItems.push(t("creationItemWorkerProfileWithType", { profileType: profileTypeValue }))
  }
  if (form.workerOrgScenario?.kind === "new") {
    creationItems.push(t("creationItemWorkerOrgWithName", { name: form.workerOrgScenario.organizationName }))
  }
  if (
    isWorkerOrgVisible &&
    workerOrgInputMode === "unknown" &&
    resolvedWorkerNameForPlaceholder
  ) {
    creationItems.push(
      t("creationItemPlaceholderOrgWithName", { name: buildPlaceholderWorkerOrgName(resolvedWorkerNameForPlaceholder, new Date()) })
    )
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const errors = validate(form, isWorkerOrgRequired, t)
  // The "unknown" placeholder bypass is honored at submit time whenever the backend will
  // actually create a fresh profile + org from the request — i.e. for create-contact and
  // add-profile. For existing-profile it would be silently dropped (the ExistingProfile
  // branch reads OrgId only when Kind == Existing); the unknown action transitions to
  // add-profile in that case, so by submit time we never see existing-profile + unknown.
  const placeholderBypassAllowed =
    form.workerScenario?.kind === "create-contact" ||
    form.workerScenario?.kind === "add-profile"
  if (workerOrgInputMode === "unknown" && placeholderBypassAllowed) {
    delete errors.workerOrgId
  }

  // Visibility for the Search/I-don't-know radio toggle. Broader than `placeholderBypassAllowed`
  // because the user needs to be able to SEE and CLICK "I don't know" while still in
  // existing-profile — clicking it transitions to add-profile, after which submission is OK.
  const canChooseUnknownMode =
    placeholderBypassAllowed ||
    (form.workerScenario?.kind === "existing-profile" && isWorkerOrgManuallyUnlocked)
  const formIsValid = Object.keys(errors).length === 0
  const isBlocked = isStatusBlocked || (isClientOrgMissing && hasNoMappedClient)
  const canSubmit =
    formIsValid &&
    !isBlocked &&
    !isEmailEditing &&
    pendingCorrections.length === 0 &&
    submitResult?.success !== true
  const showClientOrgBanner =
    isClientOrgMissing &&
    hasNoMappedClient &&
    clientOrgAutoMatchPhase === "done"

  const showError = (field: keyof PlacementFormErrors): boolean =>
    Boolean((touched[field] || submitted) && errors[field])

  const hasMarginError =
    !!form.billRate &&
    !!form.payRate &&
    !!form.billCurrencyCode &&
    !!form.payCurrencyCode &&
    form.billCurrencyCode === form.payCurrencyCode &&
    parseFloat(form.payRate) > parseFloat(form.billRate)

  const payRateCurrencyWarning =
    !!form.billRate &&
    !!form.payRate &&
    !!form.billCurrencyCode &&
    !!form.payCurrencyCode &&
    form.billCurrencyCode !== form.payCurrencyCode
      ? t("payRateCurrencyWarning")
      : undefined

  const disabledReason = isStatusBlocked
    ? t("blockedPendingChanges")
    : isEmailEditing
      ? t("confirmEmailBeforeSubmit")
      : !formIsValid
        ? t("completeAllRequiredFields")
        : null

  // Render order for the missing-fields list — matches the visual top-to-bottom order of the
  // form so the list reads the same way the user scans the page. Built once from the static
  // FIELD_LABELS map; the actual entries are filtered to only the keys present in `errors`.
  const FIELD_RENDER_ORDER: Array<keyof PlacementFormErrors> = [
    "internalCompanyId",
    "clientOrgId",
    "lobCode",
    "workerEmail",
    "profileTypeCode",
    "worker",
    "workerOrgId",
    "startDate",
    "endDate",
    "billRate",
    "payRate",
    "billCurrencyCode",
    "payCurrencyCode",
  ]
  // Currency keys live INSIDE the rate field's wrapper (the NumericField renders both the
  // amount input and a currency dropdown). Point their anchors at the rate row so click-to-
  // jump scrolls there and lands on the first focusable child of the row. Without this,
  // getElementById would find an empty marker div with no rect / no focusable child and the
  // click would silently no-op.
  const anchorKeyFor = (key: keyof PlacementFormErrors): keyof PlacementFormErrors =>
    key === "billCurrencyCode" ? "billRate"
      : key === "payCurrencyCode" ? "payRate"
        : key

  const missingFields = FIELD_RENDER_ORDER
    .filter((key) => errors[key] != null)
    .map((key) => ({
      key,
      label: t(FIELD_LABEL_KEYS[key]),
      anchorId: `${FIELD_ANCHOR_ID_PREFIX}${anchorKeyFor(key)}`,
    }))

  // ---------------------------------------------------------------------------
  // Submit / corrections
  // ---------------------------------------------------------------------------
  const lookups: ReverseMapperLookups = useMemo(() => ({ // TODO: Why is this here?
    getCodeValueIdByCode: (code, _groupName) =>
      lobs.find((l) => l.code === code)?.id ?? null,
  }), [lobs])
  // lookups is kept here for future use by onSubmit callers via the form state

  const submit = useCallback(async () => {
    setSubmitted(true)
    if (!canSubmit) return

    setIsSubmitting(true)
    setSubmitResult(null)
    setPendingCorrections([])
    try {
      // "I don't know organization name" → swap to a "new" scenario with an auto-generated
      // placeholder name so the backend creates a real org via the existing creation path.
      // Same scope as `placeholderBypassAllowed` (validation): create-contact or add-profile.
      // Both go through the AddProfile/CreateContact branch in the backend, which creates
      // the org and the profile from the request payload.
      const placeholderSwapAllowed =
        form.workerScenario?.kind === "create-contact" ||
        form.workerScenario?.kind === "add-profile"
      const formToSubmit: PlacementFormState =
        isWorkerOrgVisible && workerOrgInputMode === "unknown" && placeholderSwapAllowed
          ? {
              ...form,
              workerOrgScenario: {
                kind: "new",
                organizationName: buildPlaceholderWorkerOrgName(
                  resolvedWorkerNameForPlaceholder,
                  new Date(),
                ),
              },
            }
          : form

      const result = await onSubmit(formToSubmit)
      if (!result.ok) {
        setSubmitResult({ success: false, isConflict: false })
      } else {
        setSubmitResult(result.data)
        if (!result.data.success) {
          setPendingCorrections(await computeCorrections(result.data, form, getWorkerProfileById))
        }
      }
    } catch {
      setSubmitResult({ success: false, isConflict: false })
    } finally {
      setIsSubmitting(false)
    }
  }, [canSubmit, form, onSubmit, getWorkerProfileById, resolvedWorkerNameForPlaceholder, isWorkerOrgVisible, workerOrgInputMode, isWorkerOrgManuallyUnlocked])

  const applyCorrections = useCallback(() => {
    const patch = pendingCorrections
      .filter((c) => c.patch != null)
      .reduce((acc, c) => ({ ...acc, ...c.patch }), {} as Partial<PlacementFormState>)

    // When correcting a create-contact scenario, the server created a new contact
    // that the landscape hook doesn't know about yet — refresh it so the new
    // contact appears in the dropdown.
    const wasCreateContact = form.workerScenario?.kind === "create-contact"

    // When corrections set both workerScenario and workerOrgScenario, skip the
    // org-change effect that would re-derive the scenario and overwrite our patch.
    if (patch.workerScenario != null && patch.workerOrgScenario !== undefined) {
      skipOrgDeriveRef.current = true
    }

    setForm((prev) => ({ ...prev, ...patch }))
    if (patch.workerScenario != null) {
      setWorkerProfilesRefreshKey((k) => k + 1)
      if (wasCreateContact) {
        setLandscapeRefreshKey((k) => k + 1)
      }
    }
    if (patch.workerOrgScenario !== undefined) {
      setWorkerOrgRefreshKey((k) => k + 1)
      // Corrections always land the user back in search mode. The safety effect enforces this
      // for existing-profile / add-profile; we also choose it for create-contact so the user
      // can re-pick or re-create an org explicitly after a partial submission.
      setWorkerOrgInputMode("search")
    }
    setPendingCorrections([])
    setSubmitResult(null)
  }, [pendingCorrections, form.workerScenario])

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const selectContact = useCallback((contactId: string) => {
    if (!form.profileTypeCode) return
    setIsWorkerOrgManuallyUnlocked(false)
    const derived = deriveScenarioOnContactSelect(contactId, form.profileTypeCode, allProfiles)
    setForm((prev) => ({ ...prev, ...derived }))
    // Always start in search mode after selecting a contact. The safety effect forces this
    // for existing-profile / add-profile anyway; making it explicit here avoids a flash of
    // "unknown" UI between this update and the safety effect running.
    setWorkerOrgInputMode("search")
  }, [allProfiles, form.profileTypeCode])

  const createContact = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      workerScenario: { kind: "create-contact" },
      workerOrgScenario: null,
    }))
    // Brand-new contact: start in search mode. The user must intentionally flip to "I don't
    // know" if they want a placeholder org (the only scenario where placeholder is honored).
    setWorkerOrgInputMode("search")
  }, [])

  const undoWorkerCreation = useCallback(() => {
    setForm((prev) => ({ ...prev, workerScenario: null, workerOrgScenario: null }))
    setIsWorkerOrgManuallyUnlocked(false)
    setWorkerOrgInputMode("search")
  }, [])

  const selectWorkerOrg = useCallback((orgId: string) => {
    setForm((prev) => ({ ...prev, workerOrgScenario: { kind: "existing", organizationId: orgId } }))
    setWorkerOrgInputMode("search")
  }, [])

  // One-shot scenario switch used by the section when the user accepts the "Use existing
  // profile" affordance after trying to pick an org that's already linked to a same-type
  // profile. Sets BOTH the worker scenario (existing-profile) and the org scenario in a
  // single update so the org-change useEffect doesn't see a stale workerScenario and
  // re-derive against the wrong contact.
  const useExistingProfileForOrg = useCallback((orgId: string) => {
    const target = excludedOrgToProfile.get(orgId)
    if (!target) return
    skipOrgDeriveRef.current = true
    setIsWorkerOrgManuallyUnlocked(false)
    setForm((prev) => ({
      ...prev,
      workerScenario: {
        kind: "existing-profile",
        profileId: target.profileId,
        contactId: target.contactId,
      },
      workerOrgScenario: { kind: "existing", organizationId: orgId },
    }))
    setWorkerOrgInputMode("search")
  }, [excludedOrgToProfile])

  const createWorkerOrg = useCallback((name: string) => {
    setForm((prev) => {
      const next: PlacementFormState = {
        ...prev,
        workerOrgScenario: { kind: "new", organizationName: name },
      }
      // Manual-unlock override path: the user is creating a new org against a profile whose
      // existing org link the backend can't update via this drawer. Transition to add-profile
      // so the new org actually gets created (and a new profile linked to it) instead of being
      // silently dropped by the ExistingProfile branch.
      if (prev.workerScenario?.kind === "existing-profile") {
        next.workerScenario = { kind: "add-profile", contactId: prev.workerScenario.contactId }
      }
      return next
    })
    setWorkerOrgInputMode("search")
  }, [])

  const enterWorkerOrgSearchMode = useCallback(() => {
    setWorkerOrgInputMode("search")
    setForm((prev) => (prev.workerOrgScenario ? { ...prev, workerOrgScenario: null } : prev))
  }, [])

  const enterWorkerOrgUnknownMode = useCallback(() => {
    setWorkerOrgInputMode("unknown")
    setForm((prev) => {
      let next: PlacementFormState = prev
      if (prev.workerOrgScenario) {
        // Drop any prior pick — placeholder will be materialised at submit time.
        next = { ...next, workerOrgScenario: null }
      }
      // Same rationale as createWorkerOrg: the placeholder swap at submit time only fires for
      // create-contact / add-profile (with manual unlock). Transition existing-profile here so
      // the user's "I don't know" choice doesn't get silently dropped by the backend.
      if (prev.workerScenario?.kind === "existing-profile") {
        next = {
          ...next,
          workerScenario: { kind: "add-profile", contactId: prev.workerScenario.contactId },
        }
      }
      return next
    })
  }, [])

  const clearWorkerOrg = useCallback(() => {
    setForm((prev) => ({ ...prev, workerOrgScenario: null }))
  }, [])

  const unlockWorkerOrg = useCallback(() => {
    setIsWorkerOrgManuallyUnlocked(true)
  }, [])

  const setEmailEditing = useCallback((isEditing: boolean) => {
    setIsEmailEditing(isEditing)
  }, [])

  const setWorkerEmail = useCallback((email: string) => {
    // Re-confirming the same address (focus + accept without editing) must not
    // reset the worker / worker-org selections — only an actual change should.
    if (form.workerEmail === email) return
    setForm((prev) => ({ ...prev, workerEmail: email, workerScenario: null, workerOrgScenario: null }))
    setIsWorkerOrgManuallyUnlocked(false)
    // Different worker → fresh start. Always begin in search mode.
    setWorkerOrgInputMode("search")
  }, [form.workerEmail])

  const setInternalCompanyId = useCallback((id: string | null) => {
    setForm((prev) => ({ ...prev, internalCompanyId: id }))
  }, [])

  const setClientOrgId = useCallback((id: string | null) => {
    setForm((prev) => ({ ...prev, clientOrgId: id }))
  }, [])

  const setProfileTypeCode = useCallback((code: string | null) => {
    setForm((prev) => ({ ...prev, profileTypeCode: code }))
  }, [])

  const setStartDate = useCallback((date: string) => {
    setForm((prev) => ({ ...prev, startDate: date }))
    setTouched((prev) => ({ ...prev, startDate: true, ...(form.endDate ? { endDate: true } : {}) }))
  }, [form.endDate])

  const setEndDate = useCallback((date: string) => {
    setForm((prev) => ({ ...prev, endDate: date }))
    setTouched((prev) => ({ ...prev, endDate: true }))
  }, [])

  const setBillRate = useCallback((rate: string) => {
    setForm((prev) => ({ ...prev, billRate: rate }))
    setTouched((prev) => form.payRate ? { ...prev, payRate: true } : prev)
  }, [form.payRate])

  const setPayRate = useCallback((rate: string) => {
    setForm((prev) => ({ ...prev, payRate: rate }))
    setTouched((prev) => ({ ...prev, payRate: true }))
  }, [])

  const setBillCurrencyCode = useCallback((code: string | null) => {
    setForm((prev) => ({ ...prev, billCurrencyCode: code }))
  }, [])

  const setPayCurrencyCode = useCallback((code: string | null) => {
    setForm((prev) => ({ ...prev, payCurrencyCode: code }))
  }, [])

  const setLobCode = useCallback((code: string | null) => {
    setForm((prev) => ({ ...prev, lobCode: code }))
  }, [])

  const setTemplateV2Id = useCallback((id: string | null) => {
    setForm((prev) => ({ ...prev, templateV2Id: id }))
  }, [])

  const setWorkerPreferredCultureCode = useCallback((code: string) => {
    setForm((prev) => ({ ...prev, workerPreferredCultureCode: code }))
  }, [])

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    form,

    actions: {
      selectContact,
      createContact,
      undoWorkerCreation,
      selectWorkerOrg,
      useExistingProfileForOrg,
      createWorkerOrg,
      enterWorkerOrgSearchMode,
      enterWorkerOrgUnknownMode,
      clearWorkerOrg,
      unlockWorkerOrg,
      setEmailEditing,
      setWorkerEmail,
      setInternalCompanyId,
      setClientOrgId,
      setProfileTypeCode,
      setStartDate,
      setEndDate,
      setBillRate,
      setPayRate,
      setBillCurrencyCode,
      setPayCurrencyCode,
      setLobCode,
      setTemplateV2Id,
      setWorkerPreferredCultureCode,
      submit,
      applyCorrections,
    },

    validation: {
      errors,
      showError,
      canSubmit,
      isBlocked,
      isEmailEditing,
      showClientOrgBanner,
      submitted,
      disabledReason,
      missingFields,
      hasMarginError,
      payRateCurrencyWarning,
    },

    submission: {
      isSubmitting,
      result: submitResult,
      corrections: pendingCorrections,
      succeeded: submitResult?.success === true,
      isFormLocked: pendingCorrections.length > 0 || submitResult?.success === true,
    },

    worker: {
      allProfiles,
      contactOptions,
      selectedContactId,
      hasNameMismatch,
      isStatusBlocked,
      blockedProfileUrl: blockedProfile && fboAppUrl
        ? `${fboAppUrl}/next/contact/${blockedProfile.contactId}/profile/${blockedProfile.profileTypeCode}/${blockedProfile.id}`
        : null,
      loading: workerProfiles.loading || landscape.loading,
      showCreateContactAction,
      hasExistingProfileOfSelectedType,
      profileTypeValue,
      refreshKey: workerProfilesRefreshKey,
    },

    workerOrg: {
      isVisible: isWorkerOrgVisible,
      isRequired: isWorkerOrgRequired,
      isLocked: isWorkerOrgLocked,
      selectedProfileHasOrg: !!selectedWorker?.workerOrgId,
      inputMode: workerOrgInputMode,
      canChooseUnknownMode,
      excludedOrgIds: excludedOrgIdSet,
      placeholderName: resolvedWorkerNameForPlaceholder
        ? buildPlaceholderWorkerOrgName(resolvedWorkerNameForPlaceholder, new Date())
        : null,
      refreshKey: workerOrgRefreshKey,
    },

    search: {
      internalCompany: internalCompanySearch,
      clientOrg: clientOrgSearch,
      workerOrg: workerOrgSearchHook,
    },

    templates: {
      list: filteredTemplates,
      canSelect: !!(form.lobCode && form.clientOrgId),
      placeholder: !form.clientOrgId
        ? t("templatePlaceholderSelectClient")
        : !form.lobCode
          ? t("templatePlaceholderSelectLob")
          : filteredTemplates.length === 0
            ? t("templatePlaceholderNoTemplates")
            : t("templatePlaceholderSelect"),
    },
    creationItems,

    diffs: {
      internalCompanyChanged,
      clientOrgChanged,
      profileTypeChanged,
      workerChanged,
      workerOrgChanged,
      lobChanged,
      emailChanged,
      startDateChanged,
      endDateChanged,
      billRateChanged,
      payRateChanged,
      billCurrencyChanged,
      payCurrencyChanged,
    },
  }
}
